/* eslint-disable react/prop-types */

import * as React from 'react';

import { translate } from '~l10n/client';

import FirstPageIcon from '../icons/FirstPageIcon';
import LastPageIcon from '../icons/LastPageIcon';
import NextArrowIcon from '../icons/NextArrowIcon';
import PreviousArrowIcon from '../icons/PreviousArrowIcon';

const MAX_PAGES = 7;
/**
 * Socrative Pagination - A page transition component with the following features:
 *
 *     1. Previous and next buttons for moving backward and forward, one page at a time.
 *     2. Text for the current range of items, as well as the total number of items.
 *     3. Up to 7 page numbers for jumping directly to specific pages.
 *
 * When a page jump occurs, this component centers the target page number (if there are enough pages before and after).
 * Here's an example after jumping from page 1 to 7, when there are a total of 572 items (12 pages):
 *
 *     [<] 301-350 of 572 [>]
 *        4 5 6 [7] 8 9 10
 *
 * Properties:
 *
 *     currentPage     {number}   The page number currently being displayed
 *     itemsPerPage    {number}   The number of items displayed per page
 *     nextClicked     {function} Callback invoked when the next button is clicked
 *     pageClicked     {function} Callback invoked when any page number is clicked
 *     previousClicked {function} Callback invoked when the previous button is clicked
 *     totalItems      {number}   The total number of items to be paginated
 */
export default class Pagination extends React.Component {
  getLowPage() {
    return (
      this.props.currentPage * this.props.itemsPerPage -
      (this.props.itemsPerPage - 1)
    );
  }

  getHighPage() {
    return Math.min(
      this.props.currentPage * this.props.itemsPerPage,
      this.props.totalItems
    );
  }

  getTotalPages() {
    return Math.ceil(this.props.totalItems / this.props.itemsPerPage);
  }

  getPagesToDisplay() {
    let pagesToDisplay = [];
    let i = 1;

    if (this.props.currentPage <= 4 || this.getTotalPages() <= MAX_PAGES) {
      for (; i <= Math.min(MAX_PAGES, this.getTotalPages()); i++) {
        pagesToDisplay.push(i);
      }
    } else {
      let pageCount = 0;

      for (i = this.props.currentPage + 3; pageCount < MAX_PAGES; i--) {
        if (i <= this.getTotalPages()) {
          pagesToDisplay.push(i);
          pageCount++;
        }
      }

      pagesToDisplay.reverse();
    }

    return pagesToDisplay;
  }

  canClickNext() {
    return this.props.currentPage < this.getTotalPages();
  }

  firstClicked() {
    if (this.props.pageClicked && this.canClickPrevious()) {
      this.props.pageClicked(1);
    }
  }

  lastClicked() {
    if (this.props.pageClicked && this.canClickNext()) {
      this.props.pageClicked(this.getTotalPages());
    }
  }

  previousClicked() {
    if (this.props.previousClicked && this.canClickPrevious()) {
      this.props.previousClicked();
    }
  }

  nextClicked() {
    if (this.props.nextClicked && this.canClickNext()) {
      this.props.nextClicked();
    }
  }

  pageClicked(page) {
    if (this.props.pageClicked && page !== this.props.currentPage) {
      // eslint-disable-next-line react/prop-types
      this.props.pageClicked(page);
    }
  }

  canClickPrevious() {
    return this.props.currentPage > 1;
  }

  render() {
    let pageNumbers = this.getPagesToDisplay();
    let pageNumbersToDisplay = [];

    for (let i = 0; i < pageNumbers.length; i++) {
      let pageNumber = pageNumbers[i];

      pageNumbersToDisplay.push(
        <button
          className={
            'page-number-button' +
            (pageNumber === this.props.currentPage
              ? ' page-number-button-selected'
              : '')
          }
          key={'page-number-' + pageNumber}
          // Capture the page number in the closure.
          onClick={((pageNumber) => {
            return () => {
              this.pageClicked(pageNumber);
            };
          })(pageNumber)}
        >
          {pageNumber}
        </button>
      );
    }

    let progressText = '<b>{0}-{1}</b> <i>{2}</i> <b>{3}</b>'.format(
      this.getLowPage(),
      this.getHighPage(),
      translate('of'),
      this.props.totalItems
    );

    if (this.props.itemsPerPage === 1) {
      progressText = '<b>{0}</b> <i>{1}</i> <b>{2}</b>'.format(
        this.getLowPage(),
        translate('of'),
        this.props.totalItems
      );
    }

    return (
      <div
        className={
          'pagination-container' +
          (this.props.className ? ' ' + this.props.className : '')
        }
      >
        <div className="previous-next-container">
          <button
            className={
              'top-page-button previous-button' +
              (this.canClickPrevious() ? '' : ' disabled')
            }
            onClick={() => this.firstClicked()}
          >
            <FirstPageIcon className="first-last-page-arrow" />
          </button>
          <button
            className={
              'top-page-button previous-button' +
              (this.canClickPrevious() ? '' : ' disabled')
            }
            onClick={() => this.previousClicked()}
          >
            <PreviousArrowIcon className="next-previous-page-arrow" />
          </button>
          <span
            className="pagination-range"
            dangerouslySetInnerHTML={{ __html: progressText }}
          />
          <button
            className={
              'top-page-button next-button' +
              (this.canClickNext() ? '' : ' disabled')
            }
            onClick={() => this.nextClicked()}
          >
            <NextArrowIcon className="next-previous-page-arrow" />
          </button>
          <button
            className={
              'top-page-button next-button' +
              (this.canClickNext() ? '' : ' disabled')
            }
            onClick={() => this.lastClicked()}
          >
            <LastPageIcon className="first-last-page-arrow" />
          </button>
        </div>
        <div className="page-numbers-container">{pageNumbersToDisplay}</div>
      </div>
    );
  }
}
